import {
  array,
  boolean,
  type InferOutput,
  literal,
  looseObject,
  minLength,
  nullable,
  object,
  optional,
  parser,
  picklist,
  pipe,
  record,
  strictObject,
  string,
  transform,
  union,
  unknown,
} from "valibot";

import { parseCheckoutQuote } from "./checkout-schemas.ts";
import {
  customFieldAnswerSchema,
  nonEmptyStringSchema,
  safeIntegerSchema,
} from "./schema-helpers.ts";

const checkoutPricingEntries = {
  email: optional(string()),
  includeServiceFee: optional(boolean()),
  promoCode: optional(string()),
  tipCents: optional(safeIntegerSchema(0)),
};

const checkoutPricingInputSchema = strictObject(checkoutPricingEntries);

export const parseCheckoutPricingInput = parser(
  optional(checkoutPricingInputSchema, {})
);

const paymentSubmissionSchema = strictObject({
  confirmationToken: optional(nonEmptyStringSchema),
  customFieldAnswers: optional(array(customFieldAnswerSchema)),
  email: string(),
  memberId: optional(string()),
  merchSize: optional(picklist(["XS", "S", "M", "L", "XL", "XXL", "XXXL"])),
  metadata: optional(record(string(), string())),
  paymentMethodConfiguration: optional(literal("explicit")),
  phone: optional(string()),
  shipping: optional(
    strictObject({
      address: strictObject({
        city: string(),
        country: string(),
        line1: string(),
        line2: optional(string()),
        postal_code: string(),
        state: string(),
      }),
      name: string(),
    })
  ),
});

const checkoutItemSchema = union([
  strictObject({
    amountCents: safeIntegerSchema(1),
    productId: nonEmptyStringSchema,
  }),
  strictObject({
    productId: nonEmptyStringSchema,
    quantity: safeIntegerSchema(1),
  }),
  strictObject({
    productId: nonEmptyStringSchema,
  }),
]);

const checkoutSelectionItemSchema = union([
  strictObject({
    amountCents: safeIntegerSchema(1),
    productSlug: nonEmptyStringSchema,
  }),
  strictObject({
    productSlug: nonEmptyStringSchema,
    quantity: safeIntegerSchema(1),
  }),
  strictObject({
    productSlug: nonEmptyStringSchema,
  }),
]);

const checkoutItemsSchema = pipe(array(checkoutItemSchema), minLength(1));
const checkoutSelectionItemsSchema = pipe(
  array(checkoutSelectionItemSchema),
  minLength(1)
);

const loadCheckoutInputSchema = object({
  selection: checkoutSelectionItemsSchema,
});

const calculateTotalInputSchema = looseObject({
  catalog: optional(unknown()),
  email: optional(unknown()),
  includeServiceFee: optional(unknown()),
  items: optional(checkoutItemsSchema),
  promoCode: optional(unknown()),
  selection: optional(checkoutSelectionItemsSchema),
  tipCents: optional(unknown()),
});

const preparedCheckoutInputSchema = pipe(
  looseObject({
    authoritative: literal(true),
    items: checkoutItemsSchema,
    payment: object({
      connectedAccountId: nullable(nonEmptyStringSchema),
      currency: literal("usd"),
      publishableKey: pipe(string(), minLength(1)),
    }),
    paymentReference: pipe(string(), minLength(1)),
    pricing: checkoutPricingInputSchema,
  }),
  transform((checkout) => {
    const { items, payment, paymentReference, pricing, ...quoteInput } =
      checkout;
    const parsedQuote = parseCheckoutQuote({
      ...quoteInput,
      connectedAccountId: payment.connectedAccountId,
    });
    const { connectedAccountId, ...quote } = parsedQuote;
    return {
      items,
      pricing,
      quote: {
        ...quote,
        authoritative: true as const,
        payment: {
          connectedAccountId,
          currency: "usd" as const,
          publishableKey: payment.publishableKey,
        },
        paymentOption: parsedQuote.paymentOption,
        paymentReference,
      },
    };
  })
);

const submitPaymentInputSchema = looseObject({
  checkout: preparedCheckoutInputSchema,
  submission: paymentSubmissionSchema,
});

const paymentReferenceInputSchema = nonEmptyStringSchema;
const parseCatalogInputFields = parser(looseObject({}));

export type ParsedPreparedCheckoutInput = InferOutput<
  typeof preparedCheckoutInputSchema
>;
export type ParsedLoadCheckoutInput = InferOutput<
  typeof loadCheckoutInputSchema
>;
export type ParsedPaymentReferenceInput = InferOutput<
  typeof paymentReferenceInputSchema
>;
export type ParsedSubmitPaymentInput = InferOutput<
  typeof submitPaymentInputSchema
>;

const parseCalculateTotalInputFields = parser(calculateTotalInputSchema);

export function parseCalculateTotalInput<Input>(input: Input) {
  const { catalog, items, selection, ...pricingInput } =
    parseCalculateTotalInputFields(input);
  let parsedCatalog: ReturnType<typeof parseCatalogInputFields>;
  try {
    parsedCatalog = parseCatalogInputFields(catalog);
  } catch (error) {
    throw new TypeError("A catalog is required to calculate a checkout.", {
      cause: error,
    });
  }
  try {
    return {
      catalog: parsedCatalog,
      items,
      pricing: parseCheckoutPricingInput(pricingInput),
      selection,
    };
  } catch (error) {
    throw new TypeError("Checkout pricing input is invalid.", { cause: error });
  }
}

export type ParsedCalculateTotalInput = ReturnType<
  typeof parseCalculateTotalInput
>;
export type ParsedCheckoutPricingInput = InferOutput<
  typeof checkoutPricingInputSchema
>;

export const parseLoadCheckoutInput = parser(loadCheckoutInputSchema);
export const parsePaymentReferenceInput = parser(paymentReferenceInputSchema);
export const parsePreparedCheckoutInput = parser(preparedCheckoutInputSchema);
export const parseSubmitPaymentInput = parser(submitPaymentInputSchema);

export const parseCheckoutSelectionItems = parser(checkoutSelectionItemsSchema);
